'use client';

import { onlineOrderingProductImage } from '@trashlab/api/src/domains/pricing/shared/product-illustration';
import {
  Box,
  Flex,
  Card,
  Text,
  Heading,
  Separator,
  ScrollArea,
  Button as RButton,
} from '@radix-ui/themes';
import StorageImage from '@trashlab/sitesapp/src/organizations/fasttrac/components/shared/storage-image';
import {
  trpcConsumerGetWebsiteCart,
  trpcConsumerGetChargesForCart,
  trpcPricingGetAppliedCoupon,
  trpcConsumerWebsiteGoBackAStep,
  trpcPricingApplyCoupon,
  trpcPricingRemoveCoupon,
} from '@trashlab/api/src/generated/hooks';
import { Button } from '@trashlab/sitesapp/src/organizations/fasttrac/template/components/shared/button';
import { ShopStepper } from '@trashlab/sitesapp/src/organizations/fasttrac/components/shop/shop-stepper';
import {
  IconCalendar,
  IconInfoCircle,
  IconArrowBack,
} from '@tabler/icons-react';
import currency from 'currency.js';
import { useShopSteps } from '@trashlab/sitesapp/src/organizations/fasttrac/components/shop/steps';
import { TRPCClientError } from '@trpc/client';
import { useState } from 'react';
import { cn } from '@trashlab/sitesapp/src/@/lib/utils';

import { TLSiteShopStep } from '@trashlab/api/src/domains/consumer/types';

interface ShopStepFrameProps {
  children: React.ReactNode;
  cartId: string;
  onNext?: () => Promise<void>;
  canContinue?: boolean;
  isLoading?: boolean;
  hideBackButton?: boolean;
}

export const ShopStepFrame = ({
  children,
  cartId,
  onNext,
  canContinue = false,
  isLoading = false,
  hideBackButton = false,
}: ShopStepFrameProps) => {
  // Coupon input managed with local state instead of react-hook-form.
  // Reason: react-hook-form's register() was conflicting with the mobile input
  // (iOS/Android), preventing keystrokes from updating the value. Using a
  // controlled component with useState avoids the ref/event conflicts and makes
  // the input work reliably on mobile.
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState<string | null>(null);
  // Whether the sales tax breakdown (dual/multi-tax) is visible. Replaces the
  // Radix Tooltip that did not reliably open on touch devices.
  const [isTaxDetailsOpen, setIsTaxDetailsOpen] = useState(false);

  const { data: cartData, isLoading: cartIsLoading } =
    trpcConsumerGetWebsiteCart.useQuery({
      id: cartId,
    });

  const { data: charges, refetch: refetchCharges } =
    trpcConsumerGetChargesForCart.useQuery(
      {
        cartId: cartData?.cart.id ?? '',
        tenantId: cartData?.cart.organization.id ?? '',
      },
      {
        enabled: !!cartData?.cart.id && !!cartData?.cart.organization.id,
      },
    );

  const { data: appliedCoupon, refetch: refetchCoupon } =
    trpcPricingGetAppliedCoupon.useQuery(
      {
        cartId: cartData?.cart.id ?? '',
        tenantId: cartData?.cart.organization.id ?? '',
      },
      {
        enabled: !!cartData?.cart.id && !!cartData?.cart.organization.id,
      },
    );

  const goBackAStep = trpcConsumerWebsiteGoBackAStep.useMutation();

  // Get the steps for this organization
  const shopSteps = useShopSteps({
    tenantId: cartData?.cart.organization.id ?? '',
  });

  const applyCouponMutation = trpcPricingApplyCoupon.useMutation({
    onSuccess: () => {
      setCouponCode('');
      setCouponError(null);
    },
    onError: (error) => {
      if (error instanceof TRPCClientError) {
        setCouponError(error.message || 'Invalid or expired coupon code');
      }
    },
  });

  const removeCouponMutation = trpcPricingRemoveCoupon.useMutation();

  // Submission helper: trims + uppercases the code, skips empty input, and
  // clears any previous error before firing the mutation. Called from the
  // Apply button and from Enter key on mobile.
  const submitCoupon = () => {
    if (!cartData) return;
    const normalizedCode = couponCode.trim().toUpperCase();
    if (!normalizedCode) return;
    setCouponError(null);
    applyCouponMutation.mutate({
      code: normalizedCode,
      cartId: cartData.cart.id,
      tenantId: cartData.cart.organization.id,
    });
  };

  if (cartIsLoading || !cartData) {
    return <Box p="4">Loading cart data...</Box>;
  }

  const discountableCharges = charges?.discountableItems.map((charge) => ({
    ...charge,
    total: charge.amount * charge.quantity,
  }));

  const nonDiscountableCharges = charges?.nonDiscountableItems.map(
    (charge) => ({
      ...charge,
      total: charge.amount * charge.quantity,
    }),
  );

  const discountableTotal =
    discountableCharges?.reduce((acc, charge) => acc + charge.total, 0) ?? 0;

  const nonDiscountableTotal =
    nonDiscountableCharges?.reduce((acc, charge) => acc + charge.total, 0) ?? 0;

  const totalPrice = discountableTotal + nonDiscountableTotal;

  const formatCurrency = (amount: number) => {
    return currency(amount, { fromCents: true }).format();
  };

  const dumpsterSizeRaw = cartData.cart.product?.cubicYardage;
  // Parse cubic yardage - handle both string ("10YD", "10") and number formats
  const dumpsterSizeNum =
    typeof dumpsterSizeRaw === 'string'
      ? parseInt(dumpsterSizeRaw.replace(/\D/g, ''), 10) || 0
      : Number(dumpsterSizeRaw) || 0;
  const dumpsterSize = dumpsterSizeNum > 0 ? dumpsterSizeNum : null;

  const dumpsterImage =
    onlineOrderingProductImage(cartData.cart.product) ?? undefined;

  const daysLimit = cartData.cart.sku?.daysLimit ?? undefined;
  const tonsLimit = cartData.cart.sku?.tonsLimit ?? undefined;

  const currentStepIndex = cartData.currentStepIndex;
  const currentStep = shopSteps[currentStepIndex]?.key;

  // Check if address is considered valid/present for pricing
  const hasValidAddressForPricing =
    !!cartData.cart.siteAddress?.postalCode &&
    !!cartData.cart.siteAddress?.latitude;

  const CURRENT_PAYMENT_STEP = currentStep === TLSiteShopStep.Payment;
  const CURRENT_ORDER_SUBMITTED_STEP =
    currentStep === TLSiteShopStep.Confirmation;
  const CURRENT_INFORMATION_STEP = currentStep === TLSiteShopStep.Information;

  const renderStepSummaries = () => (
    <>
      {shopSteps.map((step, idx) => {
        if (!step.renderSummary) return null;

        return (
          <Box key={step.key}>
            {step.renderSummary({
              cartData,
            })}
          </Box>
        );
      })}
    </>
  );

  const renderPricingDetails = () => (
    <div className="shadow-md rounded-lg border border-[var(--gray-a5)]">
      <Flex
        gap="2"
        justify="between"
        align="center"
        wrap="wrap"
        className="bg-[var(--background-color-light)] p-2"
      >
        <Text weight="bold">
          {appliedCoupon ? `Coupon (${appliedCoupon.coupon.code})` : 'Discount'}
        </Text>

        {appliedCoupon ? (
          <Flex align="center" gap="2">
            <Text className="text-[var(--primary-color)]">
              -{formatCurrency(Number(appliedCoupon.discountAmount))}
            </Text>
            {!CURRENT_ORDER_SUBMITTED_STEP && (
              <RButton
                size="1"
                variant="soft"
                color="red"
                onClick={() => {
                  removeCouponMutation.mutate({
                    cartId: cartData.cart.id,
                    tenantId: cartData.cart.organization.id,
                  });
                }}
                disabled={removeCouponMutation.isLoading}
                className="px-1 py-0 text-xs"
              >
                Remove
              </RButton>
            )}
          </Flex>
        ) : (
          <Box className="flex-1 min-w-[180px]">
            {!CURRENT_ORDER_SUBMITTED_STEP && (
              <>
                <Flex gap="2" align="center">
                  {/* text-[16px] prevents iOS from auto-zooming on focus,
                      which was another mobile UX issue. No `uppercase`
                      CSS class here: it breaks IME composition on some
                      Android keyboards — we uppercase on submit instead. */}
                  <input
                    type="text"
                    name="coupon-code"
                    placeholder="Enter code"
                    autoComplete="off"
                    autoCapitalize="characters"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        submitCoupon();
                      }
                    }}
                    className="text-black text-[16px] h-[36px] flex-1 min-w-0 p-2 bg-white rounded-md border border-solid border-[#ced4da] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <RButton
                    type="button"
                    size="1"
                    onClick={submitCoupon}
                    disabled={
                      applyCouponMutation.isLoading || !couponCode.trim()
                    }
                    className={cn(
                      'bg-[var(--primary-color)] hover:bg-[var(--secondary-color)] text-white cursor-pointer font-bold',
                      applyCouponMutation.isLoading || !couponCode.trim()
                        ? 'opacity-50'
                        : '',
                    )}
                  >
                    Apply
                  </RButton>
                </Flex>
                {couponError && (
                  <Text size="1" color="red" className="mt-1">
                    {couponError}
                  </Text>
                )}
              </>
            )}
          </Box>
        )}
      </Flex>

      <Box className="p-2">
        {discountableCharges?.map(
          (charge, index) =>
            charge.type !== 'discount' &&
            charge.total !== 0 && (
              <Flex key={index} justify="between" align="center" mb="2">
                <Text size="2" color="gray">
                  {charge.description}
                </Text>
                <Text size="2">{formatCurrency(charge.total)}</Text>
              </Flex>
            ),
        )}

        {nonDiscountableCharges && nonDiscountableCharges.length > 0 && (
          <>
            {nonDiscountableCharges.map((charge, index) => {
              if (charge.type === 'tax') return null;
              if (charge.total === 0) return null;
              return (
                <Box key={index} mb="1">
                  <Flex justify="between" align="center">
                    <Text size="2" color="gray">
                      {charge.type === 'recurringCharge'
                        ? 'Recurring charge'
                        : 'Processing fee'}
                    </Text>
                    <Text size="2">{formatCurrency(charge.total)}</Text>
                  </Flex>
                  {/* Charge description rendered inline instead of a Radix
                      Tooltip: tooltips on touch don't open reliably and hide
                      information the user needs to see (e.g. tax rationale). */}
                  {charge.description && (
                    <Text size="1" color="gray" className="block">
                      {charge.description}
                    </Text>
                  )}
                </Box>
              );
            })}

            {nonDiscountableCharges.some((charge) => charge.type === 'tax' && charge.total !== 0) && (
              <Box>
                <Flex justify="between" align="center">
                  <Flex align="center" gap="1">
                    <Text size="2" color="gray">
                      Sales tax
                    </Text>
                    {/* Click-to-toggle replaces the Radix Tooltip so users on
                        mobile can actually view dual-tax breakdowns. The
                        tooltip did not open on touch and hid critical info. */}
                    <button
                      type="button"
                      onClick={() => setIsTaxDetailsOpen((prev) => !prev)}
                      aria-label="Toggle sales tax details"
                      aria-expanded={isTaxDetailsOpen}
                      className="inline-flex items-center justify-center bg-transparent border-0 p-0 cursor-pointer"
                    >
                      <IconInfoCircle size={16} className="text-blue-500" />
                    </button>
                  </Flex>
                  <Text size="2">
                    {formatCurrency(
                      nonDiscountableCharges
                        .filter((charge) => charge.type === 'tax')
                        .reduce((sum, charge) => sum + charge.total, 0),
                    )}
                  </Text>
                </Flex>
                {isTaxDetailsOpen && (
                  <Box className="mt-1 p-2 rounded-md bg-[var(--gray-a2)]">
                    {nonDiscountableCharges
                      .filter((charge) => charge.type === 'tax' && charge.total !== 0)
                      .map((charge, idx) => (
                        <Flex
                          key={`tax-${idx}`}
                          justify="between"
                          align="center"
                          mb="1"
                        >
                          <Text size="1" color="gray">
                            {charge.description}
                          </Text>
                          <Text size="1" color="gray">
                            {formatCurrency(charge.total)}
                          </Text>
                        </Flex>
                      ))}
                  </Box>
                )}
              </Box>
            )}
          </>
        )}

        <Separator size="4" my="3" />

        <Flex justify="between" align="center" mt="3">
          <Text size="4" weight="bold">
            Total
          </Text>
          <Text size="4" weight="bold">
            {formatCurrency(totalPrice)}
          </Text>
        </Flex>
      </Box>
    </div>
  );

  const renderProductCard = () => (
    <Card className="shadow-md p-2 mb-2">
      <Flex gap="4" align="center" wrap="wrap">
        {dumpsterImage && (
          <Box className="w-[100px] rounded-md overflow-hidden">
            <StorageImage
              alt="Dumpster image"
              bucket="container-product-images"
              logoPath={dumpsterImage}
              fit="contain"
              width={'100%'}
              height={'100%'}
            />
          </Box>
        )}
        <Flex direction="column" className="flex-1 min-w-[150px]">
          <Text size="2">
            {/* Use container name from settings */}
            {cartData.cart.product?.cubicYardage || 'Dumpster'}
          </Text>
          <Text size="2" weight="medium">
            {CURRENT_INFORMATION_STEP && !hasValidAddressForPricing ? (
              <>Starting at {formatCurrency(totalPrice)}</>
            ) : (
              <>{formatCurrency(totalPrice)}</>
            )}
          </Text>
        </Flex>
        <Flex direction="column" gap="1">
          {daysLimit && daysLimit > 0 && (
            <Flex align="center" gap="2">
              <Box className="p-1 rounded-md bg-blue-950/5">
                <IconCalendar size={16} />
              </Box>
              <Text size="2">{daysLimit} day rental</Text>
            </Flex>
          )}
          {tonsLimit && tonsLimit > 0 && (
            <Flex align="center" gap="2">
              <Box className="p-1 rounded-md bg-blue-950/5">
                <IconInfoCircle size={16} />
              </Box>
              <Text size="2">{tonsLimit} ton{tonsLimit > 1 ? 's' : ''} included</Text>
            </Flex>
          )}
          {charges?.additionalDaysPrice && charges.additionalDaysPrice > 0 && (
            <Flex align="center" gap="2">
              <Box className="p-1 rounded-md bg-blue-950/5">
                <IconCalendar size={16} />
              </Box>
              <Text size="2">{formatCurrency(charges.additionalDaysPrice)} / day over</Text>
            </Flex>
          )}
          {charges?.additionalTonsPrice && charges.additionalTonsPrice > 0 && (
            <Flex align="center" gap="2">
              <Box className="p-1 rounded-md bg-blue-950/5">
                <IconInfoCircle size={16} />
              </Box>
              <Text size="2">{formatCurrency(charges.additionalTonsPrice)} / ton over</Text>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Card>
  );

  return (
    <Card
      size="3"
      className="w-full max-w-full p-0 overflow-hidden flex flex-col h-full bg-[var(--background-color-light)]"
    >
      <Flex className="w-full flex-1 overflow-hidden flex-col md:flex-row">
        <Box className="w-full md:w-[60%] relative flex flex-col overflow-hidden bg-white p-4">
          {cartData.cart.organization.logoPath &&
            (cartData.cart.shop?.showLogoOnShop ?? true) && (
            <div className="w-[100px] h-[40px] md:w-[120px] md:h-[44px] overflow-hidden">
              <StorageImage
                alt="Organization Logo"
                bucket="org-logos"
                logoPath={cartData.cart.organization.logoPath}
                className="w-full h-full object-contain"
              />
            </div>
          )}

          <ShopStepper currentStepIndex={currentStepIndex} />

          <ScrollArea
            className="custom-scrollbar p-2 flex-1"
            scrollbars="vertical"
          >
            {children}

            {/* Mobile Summary - only visible on mobile */}
            <Box className="md:hidden">
              <Heading size="4">Order Summary</Heading>
              <Separator size="4" my="4" />

              <Box className="flex flex-col">
                {renderProductCard()}
                {renderStepSummaries()}
                {renderPricingDetails()}
              </Box>
            </Box>
          </ScrollArea>

          <Flex align="center" gap="2" className="w-full px-4 py-3">
            {!hideBackButton && (
              <Box className="flex">
                <Button
                  variant="soft"
                  onClick={() => {
                    goBackAStep.mutate({
                      cartId,
                      tenantId: cartData.cart.organization.id,
                    });
                  }}
                  loading={goBackAStep.isLoading}
                  leftIcon={<IconArrowBack />}
                >
                  Back
                </Button>
              </Box>
            )}
            {onNext && (
              <Box
                className={
                  currentStepIndex !== 0 && !hideBackButton
                    ? 'flex-1'
                    : 'w-full'
                }
              >
                <Button
                  onClick={async () => {
                    // Track step completion for analytics
                    if (
                      typeof window !== 'undefined' &&
                      (window as any).analytics
                    ) {
                      (window as any).analytics.trackEvent({
                        eventType: 'shop_step_complete',
                        metadata: {
                          cartId,
                          step: currentStep,
                        },
                      });
                    }

                    if (onNext) {
                      await onNext();
                    }
                  }}
                  disabled={!canContinue || isLoading}
                  loading={isLoading}
                  className="w-full"
                  withArrow={CURRENT_PAYMENT_STEP}
                >
                  {CURRENT_PAYMENT_STEP
                    ? 'Complete checkout'
                    : CURRENT_ORDER_SUBMITTED_STEP
                    ? 'Continue shopping'
                    : 'Continue checkout'}
                </Button>
              </Box>
            )}
          </Flex>
        </Box>

        {/* Desktop Summary - only visible on desktop */}
        <Box className="hidden md:flex w-[40%] relative flex-col overflow-hidden p-4">
          <Heading size="4">Order Summary</Heading>
          <Separator size="4" my="4" />

          <Box className="flex-1 flex flex-col overflow-auto">
            {renderProductCard()}

            <ScrollArea className="flex-1">{renderStepSummaries()}</ScrollArea>

            {renderPricingDetails()}
          </Box>
        </Box>
      </Flex>
    </Card>
  );
};
